/**
 * Add / edit a meal: name, notes, a photo (R2), and the ingredients that feed the
 * grocery list. Each ingredient carries the aisle it lives in, which is what lets
 * the list group itself the way the store is actually walked.
 */
import { useEffect, useRef, useState } from 'react';
import type { Aisle, IngredientInput, ParsedRecipe, Recipe } from '../../shared/types';
import { errMsg, hostOf, mealsApi, uploadMealPhoto, type SaveRecipeInput } from './mealsApi';
import {
  ErrorNote,
  MealPhoto,
  Modal,
  btnDanger,
  btnGhost,
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
} from './ui';

interface Row {
  key: number;
  name: string;
  quantity: string;
  unit: string;
  aisle_id: string;
  pantry: boolean;
}

const MAX_PHOTO_BYTES = 8 * 1024 * 1024; // keep in step with worker/routes/meals.ts

/** Formats a browser will actually paint. Keep in step with worker/routes/meals.ts. */
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Refused, not accepted-and-then-unshowable. The Worker refuses these too — this
 * catches it before the file is even held for upload, so the answer is instant
 * instead of arriving as a 415 at Save. Same list and same sentence as
 * src/modules/profile/AvatarField.tsx.
 */
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_HELP =
  'iPhone HEIC photos don’t show up in web browsers. On your iPhone: Settings → Camera → Formats → “Most Compatible”, then take the photo again — or open the photo, tap Edit, and save a copy as a JPEG.';

let rowSeq = 0;
function blankRow(): Row {
  return { key: ++rowSeq, name: '', quantity: '', unit: '', aisle_id: '', pantry: false };
}

type SeedIngredient = {
  name: string; quantity: number | null; unit: string | null;
  aisle_id: number | null; pantry?: boolean;
};

function rowsFrom(ingredients: SeedIngredient[]): Row[] {
  const existing = ingredients.map((i) => ({
    key: ++rowSeq,
    name: i.name,
    quantity: i.quantity === null ? '' : String(i.quantity),
    unit: i.unit ?? '',
    aisle_id: i.aisle_id === null ? '' : String(i.aisle_id),
    pantry: i.pantry === true,
  }));
  return existing.length > 0 ? existing : [blankRow()];
}

export default function MealEditor({
  meal,
  draft,
  aisles,
  onSaved,
  onDeleted,
  onClose,
}: {
  meal: Recipe | null;
  /** A recipe parsed from a URL, waiting to be reviewed and saved as a new meal. */
  draft?: ParsedRecipe | null;
  aisles: Aisle[];
  onSaved: (meal: Recipe) => void;
  onDeleted?: (id: number) => void;
  onClose: () => void;
}) {
  /**
   * The meal as it exists on the server: the one being edited, or — once a "New
   * meal" save succeeds — the row that was just created. Everything that acts on a
   * persisted meal (photo upload, photo removal, delete) goes through this, not
   * through the `meal` prop. That is what makes a failed photo upload retryable:
   * the second Save updates the row it already created instead of creating a
   * duplicate one.
   */
  const [saved, setSaved] = useState<Recipe | null>(meal);
  const [name, setName] = useState(meal?.name ?? draft?.name ?? '');
  const [notes, setNotes] = useState(meal?.notes ?? '');
  const [servings, setServings] = useState(() => {
    const s = meal?.servings ?? draft?.servings ?? null;
    return s === null ? '' : String(s);
  });
  const [instructions, setInstructions] = useState(meal?.instructions ?? draft?.instructions ?? '');
  // The imported-from link. Carried through save so the recipe view can link out.
  const [sourceUrl] = useState<string | null>(meal?.source_url ?? draft?.source_url ?? null);
  const [rows, setRows] = useState<Row[]>(() =>
    rowsFrom(meal?.ingredients ?? draft?.ingredients ?? [])
  );
  const [photoUrl, setPhotoUrl] = useState<string | null>(meal?.photo_url ?? null);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  /**
   * This modal scrolls, and the error sits at the top of it. Save and "Suggest
   * ingredients" are both way down at the bottom — so without this, a failure is
   * announced off-screen and the button just looks dead. `block: 'nearest'` is a
   * no-op when the note is already visible.
   */
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  function choosePhoto(file: File | undefined) {
    if (!file) return;
    const type = file.type.toLowerCase();
    if (HEIC_TYPES.includes(type)) {
      setError(HEIC_HELP);
      return;
    }
    if (!PHOTO_TYPES.includes(type)) {
      setError('That file is not a picture this app can show. Use a JPEG, PNG, WEBP or GIF.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError('That photo is over 8MB. Pick a smaller one.');
      return;
    }
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setPendingPhoto(file);
  }

  async function removePhoto() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingPhoto(null);
    if (fileInput.current) fileInput.current.value = '';
    if (!saved?.photo_key) {
      setPhotoUrl(null);
      return;
    }
    try {
      const updated = await mealsApi.removePhoto(saved.id);
      setSaved(updated);
      setPhotoUrl(updated.photo_url);
      onSaved(updated);
    } catch (err) {
      setError(errMsg(err));
    }
  }

  function updateRow(key: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  /**
   * Ask for a draft ingredient list built from the meal's name. It lands in the rows
   * below as ordinary editable text, aisles already assigned — a starting point to
   * correct, not an answer. Nothing reaches the database until Save.
   */
  async function suggest() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the meal a name first — the suggestion is built from it.');
      return;
    }

    // Send servings only when it is a number the cook actually typed; a half-finished
    // field would otherwise size the whole list wrong.
    const typed = servings.trim() === '' ? null : Number(servings);
    const sized = typed !== null && Number.isFinite(typed) && typed > 0 ? Math.trunc(typed) : null;

    setSuggesting(true);
    setError(null);
    try {
      const result = await mealsApi.suggestIngredients({
        name: trimmed,
        servings: sized,
        notes: notes.trim() || null,
        instructions: instructions.trim() || null,
      });
      setRows(rowsFrom(result.ingredients));
      // Blank, not merely unusable: `sized` is also null for "abc" or "0", and
      // silently replacing what the cook typed is not this button's job.
      if (servings.trim() === '' && result.servings !== null) setServings(String(result.servings));
      setConfirmReplace(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSuggesting(false);
    }
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the meal a name.');
      return;
    }

    const ingredients: IngredientInput[] = rows
      .filter((r) => r.name.trim() !== '')
      .map((r) => ({
        name: r.name.trim(),
        quantity: r.quantity.trim() === '' ? null : Number(r.quantity),
        unit: r.unit.trim() === '' ? null : r.unit.trim(),
        aisle_id: r.aisle_id === '' ? null : Number(r.aisle_id),
        pantry: r.pantry,
      }));

    if (ingredients.some((i) => i.quantity !== null && !Number.isFinite(i.quantity))) {
      setError('Quantities need to be numbers (or left blank).');
      return;
    }

    let servingsValue: number | null = null;
    if (servings.trim() !== '') {
      const n = Number(servings);
      if (!Number.isFinite(n) || n <= 0) {
        setError('Servings needs to be a whole number (or left blank).');
        return;
      }
      servingsValue = Math.trunc(n);
    }

    const input: SaveRecipeInput = {
      name: trimmed,
      notes: notes.trim() || null,
      instructions: instructions.trim() || null,
      servings: servingsValue,
      source_url: sourceUrl,
      ingredients,
    };

    setSaving(true);
    setError(null);
    try {
      // Write the row first, and record it before touching R2. If the upload below
      // throws, the meal still exists: the parent already knows about it, and the
      // next Save updates it instead of creating a second one.
      const written = saved
        ? await mealsApi.updateMeal(saved.id, input)
        : await mealsApi.createMeal(input);
      setSaved(written);
      onSaved(written);

      if (pendingPhoto) {
        const withPhoto = await uploadMealPhoto(written.id, pendingPhoto);
        setSaved(withPhoto);
        setPhotoUrl(withPhoto.photo_url);
        setPendingPhoto(null);
        setPreviewUrl(null); // effect cleanup revokes the object URL
        if (fileInput.current) fileInput.current.value = '';
        onSaved(withPhoto);
      }

      onClose();
    } catch (err) {
      setError(errMsg(err));
      setSaving(false);
    }
  }

  async function destroy() {
    if (!saved) return;
    setSaving(true);
    setError(null);
    try {
      await mealsApi.deleteMeal(saved.id);
      onDeleted?.(saved.id);
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setSaving(false);
    }
  }

  const shownPhoto = previewUrl ?? photoUrl;
  // A single blank starter row is "no ingredients", not an ingredient — so asking for
  // suggestions on a fresh meal doesn't stop to confirm overwriting nothing.
  const filledRows = rows.filter((r) => r.name.trim() !== '').length;
  const hasIngredients = filledRows > 0;

  return (
    <Modal
      title={saved ? 'Edit meal' : draft ? 'Review imported recipe' : 'New meal'}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          {saved ? (
            confirmDelete ? (
              <button type="button" className={btnDanger} onClick={destroy} disabled={saving}>
                Really remove?
              </button>
            ) : (
              <button
                type="button"
                className={btnGhost}
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                Remove
              </button>
            )
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className={btnPrimary} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save meal'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div ref={errorRef}>{error && <ErrorNote message={error} />}</div>

        {draft && !saved && (
          <div className="rounded-xl border border-dusk/25 bg-dusk-soft p-3 text-sm text-ink-soft">
            <p className="font-medium text-ink">Read from the link — give it a look.</p>
            <p className="mt-0.5">
              Check the ingredients and steps, tidy anything the reader got wrong, set each
              ingredient's aisle, then save it to your box.
            </p>
          </div>
        )}

        {/* photo */}
        <div>
          <span className={labelCls}>Photo</span>
          <div className="flex items-center gap-4">
            <MealPhoto
              name={name || 'Meal'}
              url={shownPhoto}
              className="size-20 shrink-0 rounded-xl"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={btnSecondary}
                onClick={() => fileInput.current?.click()}
              >
                {shownPhoto ? 'Replace photo' : 'Add photo'}
              </button>
              {shownPhoto && (
                <button type="button" className={btnGhost} onClick={removePhoto}>
                  Remove photo
                </button>
              )}
            </div>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={PHOTO_ACCEPT}
            className="sr-only"
            aria-label="Meal photo"
            onChange={(e) => choosePhoto(e.target.files?.[0])}
          />
          {pendingPhoto && (
            <p className="mt-2 text-xs text-ink-faint">
              {pendingPhoto.name} — uploads when you save.
            </p>
          )}
        </div>

        {/* name + servings */}
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[12rem] flex-1">
            <label className={labelCls} htmlFor="meal-name">
              Name
            </label>
            <input
              id="meal-name"
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sheet-pan chicken"
              autoComplete="off"
            />
          </div>
          <div className="w-28">
            <label className={labelCls} htmlFor="meal-servings">
              Servings
            </label>
            <input
              id="meal-servings"
              className={inputCls}
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              placeholder="4"
              inputMode="numeric"
              autoComplete="off"
            />
          </div>
        </div>

        {sourceUrl && (
          <p className="-mt-2 text-xs text-ink-faint">
            Imported from{' '}
            <a
              href={sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-dusk underline underline-offset-2 hover:text-dusk/80"
            >
              {hostOf(sourceUrl)}
            </a>
          </p>
        )}

        <div>
          <label className={labelCls} htmlFor="meal-notes">
            Notes <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <textarea
            id="meal-notes"
            className={`${inputCls} min-h-[80px]`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="425° for 35 min. Kids like it with rice."
          />
        </div>

        {/* steps */}
        <div>
          <label className={labelCls} htmlFor="meal-instructions">
            Instructions <span className="font-normal text-ink-faint">(one step per line)</span>
          </label>
          <textarea
            id="meal-instructions"
            className={`${inputCls} min-h-[140px]`}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder={'Heat the oven to 425°.\nToss the veg with oil and salt.\nRoast 30 minutes.'}
          />
        </div>

        {/* ingredients */}
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <span className={`${labelCls} mb-0`}>Ingredients</span>
            <span className="text-xs text-ink-faint">These build the grocery list</span>
          </div>

          {/*
            A meal saved as a bare name contributes NOTHING to the week's grocery
            list, and nothing says so — the list just comes up short. Rather than
            type fifteen lines to fix that, ask for a draft and correct it. The
            answer is a guess, which is why it lands in the fields below for review
            instead of being saved.
          */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-canvas px-3 py-2">
            <p className="text-xs text-ink-soft">
              {hasIngredients
                ? 'Not quite right? Ask for a suggested list instead.'
                : 'Nothing here yet — so this meal adds nothing to the grocery list.'}
            </p>
            {confirmReplace && hasIngredients ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => setConfirmReplace(false)}
                  disabled={suggesting}
                >
                  Keep mine
                </button>
                <button
                  type="button"
                  className={btnDanger}
                  onClick={() => void suggest()}
                  disabled={suggesting}
                >
                  {suggesting ? 'Thinking…' : `Replace all ${filledRows}`}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => (hasIngredients ? setConfirmReplace(true) : void suggest())}
                disabled={suggesting || name.trim() === ''}
              >
                {suggesting ? 'Thinking…' : 'Suggest ingredients'}
              </button>
            )}
          </div>

          <ul className="space-y-3">
            {rows.map((row, idx) => (
              <li key={row.key} className="rounded-xl border border-border bg-canvas p-3">
                <div className="flex items-center gap-2">
                  <input
                    className={inputCls}
                    value={row.name}
                    onChange={(e) => updateRow(row.key, { name: e.target.value })}
                    placeholder="Yellow onion"
                    aria-label={`Ingredient ${idx + 1} name`}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={btnGhost}
                    aria-label={`Remove ingredient ${idx + 1}`}
                    onClick={() =>
                      setRows((prev) => {
                        const next = prev.filter((r) => r.key !== row.key);
                        return next.length > 0 ? next : [blankRow()];
                      })
                    }
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-[5rem_1fr_1fr]">
                  <input
                    className={inputCls}
                    value={row.quantity}
                    onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                    placeholder="2"
                    inputMode="decimal"
                    aria-label={`Ingredient ${idx + 1} quantity`}
                  />
                  <input
                    className={inputCls}
                    value={row.unit}
                    onChange={(e) => updateRow(row.key, { unit: e.target.value })}
                    placeholder="lb"
                    aria-label={`Ingredient ${idx + 1} unit`}
                    autoComplete="off"
                  />
                  <select
                    className={`${inputCls} col-span-2 sm:col-span-1`}
                    value={row.aisle_id}
                    disabled={row.pantry}
                    onChange={(e) => updateRow(row.key, { aisle_id: e.target.value })}
                    aria-label={`Ingredient ${idx + 1} aisle`}
                  >
                    <option value="">No aisle</option>
                    {aisles.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/*
                  Part of the recipe, off the shopping list. A rub or a dredge belongs in
                  the ingredients — but nobody wants "1.5 tsp paprika" on the list every
                  week the meal is planned. The aisle picker goes dark because an
                  ingredient that never reaches the list has no aisle to be in.
                */}
                <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-ink-soft">
                  <input
                    type="checkbox"
                    checked={row.pantry}
                    onChange={(e) => updateRow(row.key, { pantry: e.target.checked })}
                    className="size-4 rounded border-border text-sage accent-sage focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
                  />
                  Already have it — keep off the grocery list
                </label>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={`${btnSecondary} mt-3 w-full`}
            onClick={() => setRows((prev) => [...prev, blankRow()])}
          >
            <span aria-hidden="true">＋</span> Add ingredient
          </button>
        </div>
      </div>
    </Modal>
  );
}
